import { useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Container from "react-bootstrap/esm/Container";
import Card from "react-bootstrap/esm/Card";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import AppFooter from "../footer/AppFooter";
import { Globals } from "../globals";

type FormState = "form" | "submitting" | "success" | "error";

export default function DataDeletionRequest() {
    const { t } = useTranslation();
    const [formState, setFormState] = useState<FormState>("form");
    const [email, setEmail] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormState("submitting");
        try {
            const requestDataDeletion = httpsCallable<{ email: string }, { success: boolean }>(
                functions,
                "requestDataDeletion"
            );
            await requestDataDeletion({ email: email.trim() });
            setFormState("success");
        } catch (err) {
            // Rate-limited public form (GOD-51).
            const limited = (err as { code?: string })?.code === "functions/resource-exhausted";
            setErrorMessage(t(limited ? "errors.tooManyRequests" : "errors.anErrorOccurred") as string);
            setFormState("error");
        }
    };

    if (formState === "success") {
        return (
            <Container className="mt-5" style={{ maxWidth: "600px" }}>
                <Alert variant="success">
                    <Alert.Heading>{t("dataDeletion.successTitle")}</Alert.Heading>
                    <p className="mb-0">{t("dataDeletion.successMessage")}</p>
                </Alert>
                <AppFooter />
            </Container>
        );
    }

    if (formState === "error") {
        return (
            <Container className="mt-5" style={{ maxWidth: "600px" }}>
                <Alert variant="danger">
                    <Alert.Heading>{t("dataDeletion.errorTitle")}</Alert.Heading>
                    <p>{errorMessage}</p>
                    <Button variant="outline-danger" size="sm" onClick={() => setFormState("form")}>
                        {t("dataDeletion.backToForm")}
                    </Button>
                </Alert>
                <AppFooter />
            </Container>
        );
    }

    return (
        <Container className="mt-4" style={{ maxWidth: "600px" }}>
            <Card>
                <Card.Header>
                    <h4 className="mb-0">{t("dataDeletion.heading")}</h4>
                </Card.Header>
                <Card.Body>
                    <p>{t("dataDeletion.description")}</p>

                    <Alert variant="warning" className="mb-3">
                        <strong>{t("dataDeletion.warningTitle")}:</strong>{" "}
                        {t("dataDeletion.warningText")}
                    </Alert>

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("dataDeletion.emailLabel")}</Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t("dataDeletion.emailPlaceholder") as string}
                                required
                                autoComplete="email"
                            />
                            <Form.Text className="text-muted">
                                {t("dataDeletion.emailHelp", { adminEmail: Globals.ADMIN_EMAIL })}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Check
                                type="checkbox"
                                id="deletion-confirm"
                                label={t("dataDeletion.confirmLabel")}
                                checked={confirmed}
                                onChange={(e) => setConfirmed(e.target.checked)}
                                required
                            />
                        </Form.Group>

                        <Button
                            type="submit"
                            variant="danger"
                            disabled={!email.trim() || !confirmed || formState === "submitting"}
                        >
                            {formState === "submitting" ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    {t("common.loading")}
                                </>
                            ) : (
                                t("dataDeletion.submitButton")
                            )}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>

            <p className="text-muted text-center mt-3" style={{ fontSize: "0.85em" }}>
                {t("dataDeletion.adminNote")}
            </p>
            <AppFooter />
        </Container>
    );
}
